import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { Link } from "../../../../common/Link";
import { isoWebPageURL } from "../../../../models/nominals";
import { IUser } from "../../../../models/user";
import { usePageURL } from "../../../../utils/urls";
import { LoginForm } from "../LoginForm";

import styles from "./index.module.scss";

enum Page {
    INITIAL,
    LOGIN_FORM,
}

export const AccountMenuContent = (props: {
    user: IUser | null;
}): React.ReactNode => {
    const [page, setPage] = useState<Page>(Page.INITIAL);
    const loginPopoverRef = useRef<HTMLDivElement>(null);
    const accountPopoverRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hasMountedRef = useRef(false);

    const homeURL = usePageURL("home");
    const lookupOrdersURL =
        usePageURL("customer_lookup_orders_by_email") || homeURL;
    const customerServiceURL = usePageURL("customer_service") || homeURL;

    const setModalHeight = () => {
        const elem =
            page === Page.LOGIN_FORM
                ? loginPopoverRef.current
                : accountPopoverRef.current;
        const containerElem = containerRef.current;
        if (!elem || !containerElem) {
            return;
        }
        containerElem.style.height = `${elem.scrollHeight}px`;
    };

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        setModalHeight();
    });

    const onGotoLogin = () => {
        setPage(Page.LOGIN_FORM);
    };

    const onGotoList = () => {
        setPage(Page.INITIAL);
    };

    const renderProfileLinkList = () => {
        const hasEmail = props.user && props.user.email;
        const hasPassword = props.user && props.user.has_password;

        const ordersURL = isoWebPageURL.wrap(reverse("customer:order-list"));
        const myAccountURL = isoWebPageURL.wrap(
            reverse("customer:profile-view"),
        );
        const logoutURL = isoWebPageURL.wrap(reverse("customer:logout"));

        const ordersLink = (
            <li
                className={`${styles.accountMenuPane1ListItem} ${styles.accountMenuPane1ListItemFirst}`}
            >
                <Link target="_top" href={ordersURL}>
                    {t`Orders`}
                </Link>
            </li>
        );
        const lookupOrdersLink = lookupOrdersURL ? (
            <li className={styles.accountMenuPane1ListItem}>
                <Link target="_top" href={lookupOrdersURL}>
                    {t`Check Order Status`}
                </Link>
            </li>
        ) : null;
        const customerServiceLink = customerServiceURL ? (
            <li className={styles.accountMenuPane1ListItem}>
                <Link target="_top" href={customerServiceURL}>
                    {t`Help Center`}
                </Link>
            </li>
        ) : null;
        const myAccountLink = (
            <li className={styles.accountMenuPane1ListItem}>
                <Link target="_top" href={myAccountURL}>
                    {t`My Account`}
                </Link>
            </li>
        );
        const logoutLink = (
            <li
                className={`${styles.accountMenuPane1ListItem} ${styles.accountMenuPane1ListItemLast}`}
            >
                <Link target="_top" href={logoutURL}>
                    {t`Log Out`}
                </Link>
            </li>
        );
        const loginLink = (
            <li
                className={`${styles.accountMenuPane1ListItem} ${styles.accountMenuPane1ListItemLast}`}
            >
                <button type="button" onClick={onGotoLogin}>
                    {t`Login/Create Account`}
                </button>
            </li>
        );
        return (
            <ul className={styles.accountMenuPane1List}>
                {hasEmail ? ordersLink : lookupOrdersLink}
                {hasEmail && myAccountLink}
                {customerServiceLink}
                {hasPassword ? logoutLink : loginLink}
            </ul>
        );
    };

    const modalClasses = classNames({
        [styles.accountMenu]: true,
    });
    const pane1Classes = classNames({
        [styles.accountMenuPane1]: true,
        [styles.accountMenuPaneHidden]: page !== Page.INITIAL,
    });
    const pane2Classes = classNames({
        [styles.accountMenuPane2]: true,
        [styles.accountMenuPaneHidden]: page !== Page.LOGIN_FORM,
    });
    const accountPopover = (
        <div className={pane1Classes} ref={accountPopoverRef}>
            {renderProfileLinkList()}
        </div>
    );
    const login = (
        <div className={pane2Classes} ref={loginPopoverRef}>
            <h2 className={styles.accountMenuPane2Title}>{t`Login`}</h2>
            <LoginForm
                onAfterLoad={setModalHeight}
                onAfterSave={setModalHeight}
            />
        </div>
    );
    const modalContent = (
        <div className={modalClasses} ref={containerRef}>
            <h1 className="ada-screenreader-only">{t`Support`}</h1>
            {page === Page.LOGIN_FORM && (
                <button className={styles.returnLink} onClick={onGotoList}>
                    {t`Go Back`}
                </button>
            )}
            {accountPopover}
            {login}
        </div>
    );
    return <>{modalContent}</>;
};
